import { StorageAdapter } from '../storage/StorageAdapter'
import { rowsOf } from '../storage/rows'
import { parseMaybeJson } from '../storage/json-codec'
import { dbgWarn } from '../logger'

/**
 * OPTIMIZACIÓN O(n²) del sync de occurrences (Opción B, acotada). PURO (solo storage/logger) para poder
 * testearlo aislado — la lógica de la cota boundarySeq casi introdujo una pérdida silenciosa (cazada en
 * review), así que va con test propio (test/offline-v2/sibling-sync-collapse.spec.js).
 *
 * Contexto: cada answer y cada addon-foto encola su PROPIO op occurrence.syncOccurrence (client_uuid
 * por-item), y el handler (sync/modules/occurrences.js) RE-LEE answers+addons WHERE occurrence_id y
 * RE-ENVÍA el batch COMPLETO en CADA op -> 186+99 = 256 ops × batch íntegro = ~40 min. Tras UN envío
 * exitoso, los DEMÁS syncOccurrence pending de la MISMA occurrence QUE YA EXISTÍAN al tomar el snapshot
 * (seq <= boundarySeq) son REDUNDANTES (su answer/foto ya viajó en este multipart; server idempotente
 * updateOrCreate). Se marcan 'done' -> 1 envío en vez de N.
 *
 * COTA boundarySeq (CERO-PÉRDIDA): boundarySeq = MAX(seq) del outbox capturado ANTES del snapshot de
 * answers/addons. Un op con seq > boundarySeq se encoló DESPUÉS del snapshot (el operador siguió llenando
 * durante el vuelo HTTP): su answer/foto NO viajó en este batch -> se DEJA pending y re-drena idempotente.
 * Sin la cota, marcar 'done' un op más nuevo purgaría su binario sin haberlo subido = pérdida silenciosa.
 *
 * SEGURO además: SOLO status='pending' (nunca la fila in_flight actual ni 'dead' —su pérdida ya es
 * visible en el drawer y su binario lo preserva el guard 'dead' de la purga); done_at=now respeta el
 * DONE_GRACE_MS de 5 min de la purga (evita la carrera de reingreso); last_error=NULL iguala la forma
 * 'done' de commitSuccess; occurrence_id se filtra en JS (vive dentro del body JSON). Best-effort: un
 * fallo aquí solo deja siblings pending, que re-drenan idempotente (peor caso = comportamiento actual).
 *
 * EDICIÓN DURANTE EL VUELO (TKT-2363): la cota por seq NO basta. Cuando el usuario vuelve a editar una
 * respuesta/addon cuyo op sigue pending, el coalesce (BaseRepository._saveOptimisticTx) actualiza ESA
 * fila en sitio: mismo seq (<= boundarySeq) pero valor nuevo que no viajó en este batch. Marcarla done
 * perdía la corrección: el overlay de reingreso deja de pintarla y, si el siguiente envío no sale (red
 * intermitente), nunca llega al server. Por eso el handler toma `snapshot` (seq + mutation_uuid de cada
 * sibling pending) ANTES de leer answers/addons, y aquí solo se colapsan los siblings cuyo mutation_uuid
 * sigue igual: el coalesce lo regenera en cada edición. La comparación va en el mismo UPDATE, así que
 * una edición que entra entre el SELECT y el UPDATE tampoco se pierde.
 *
 * @param {string|number} occId  occurrence del batch recién enviado con éxito.
 * @param {number} currentSeq    seq de la fila que se está drenando (in_flight); nunca se toca.
 * @param {number} boundarySeq   MAX(seq) del outbox al momento del snapshot; cota superior inclusiva.
 * @param {{ db?: object, snapshot?: Array<{seq:number, mutation_uuid:string}> }} deps  db inyectable
 *   (default StorageAdapter) y el snapshot de snapshotSiblingSyncOps. OBLIGATORIO: sin él no hay forma
 *   segura de saber qué viajó (colapsar solo por seq es justo lo que perdía correcciones), así que no se
 *   colapsa nada y los siblings re-drenan idempotente.
 */
// syncOccurrence pending de la occurrence, sin la fila en vuelo (occurrence_id vive dentro del body JSON).
async function pendingSiblings (occId, currentSeq, db) {
  const pend = rowsOf(await db.query(
    "SELECT seq, mutation_uuid, body FROM outbox WHERE op='occurrence.syncOccurrence' AND status='pending'"
  ))
  return pend
    .filter(r => r.seq !== currentSeq)
    .filter(r => { const b = parseMaybeJson(r.body); return b && String(b.occurrence_id) === String(occId) })
}

export async function markSiblingSyncOpsDone (occId, currentSeq, boundarySeq, { db = StorageAdapter, snapshot } = {}) {
  try {
    // Sin foto de mutation_uuid no se puede saber qué viajó: no colapsar (el costo es solo reenvío idempotente).
    if (!snapshot) return
    const mutationBySeq = new Map(snapshot.map(r => [r.seq, r.mutation_uuid]))
    const pairs = (await pendingSiblings(occId, currentSeq, db))
      .filter(r => r.seq <= boundarySeq && mutationBySeq.has(r.seq))
      .map(r => r.seq)
    const now = Date.now()
    // Lotes de 200 pares (401 variables): holgado bajo el límite de 999 de los SQLite viejos.
    for (let i = 0; i < pairs.length; i += 200) {
      const chunk = pairs.slice(i, i + 200)
      const ph = chunk.map(() => '(seq=? AND mutation_uuid=?)').join(' OR ')
      await db.run(
        `UPDATE outbox SET status='done', done_at=?, last_error=NULL WHERE (${ph}) AND status='pending'`,
        [now, ...chunk.flatMap(seq => [seq, mutationBySeq.get(seq)])],
        true
      )
    }
  } catch (e) {
    dbgWarn('sync', 'markSiblingSyncOpsDone falló (ignorado; siblings re-drenarán idempotente)', e)
  }
}

/**
 * Durabilidad de la cobertura de respuestas en dead-letter (TKT-2499, review Victayy). Tras un envío
 * exitoso de occurrence.syncOccurrence, el handler ya re-mandó TODAS las respuestas vigentes de la
 * occurrence, así que un op de RESPUESTA que había quedado `dead` con seq <= boundarySeq ya viajó en
 * este batch íntegro: su dato está en el server. Marcarlo `done` hace la cobertura DURABLE (no depende
 * de una fila `done` hermana que prune.js poda a los 7 días, lo que reabriría el bloqueo permanente del
 * correo/PDF) y además lo saca del drawer de sync. Acotado a ops de RESPUESTA (client_uuid en answers)
 * para no tocar cierres; la exposición de fotos omitidas en lote >20 es la MISMA que ya asume
 * markSiblingSyncOpsDone con los pending, cubierta por reportMissingPhotos. SOLO LECTURA/UPDATE del outbox.
 */
export async function markDeadAnswerSyncOpsDone (occId, boundarySeq, { db = StorageAdapter } = {}) {
  try {
    const now = Date.now()
    await db.run(
      "UPDATE outbox SET status='done', done_at=?, last_error=NULL " +
      "WHERE op='occurrence.syncOccurrence' AND status='dead' AND seq <= ? " +
      'AND client_uuid IN (SELECT client_uuid FROM answers WHERE occurrence_id = ?)',
      [now, boundarySeq, String(occId)],
      true
    )
  } catch (e) {
    dbgWarn('sync', 'markDeadAnswerSyncOpsDone falló (ignorado; el dead sigue visible en el drawer)', e)
  }
}

/**
 * Foto de los siblings pending de la occurrence (seq + mutation_uuid) para markSiblingSyncOpsDone. Se
 * toma ANTES de leer answers/addons: lo que se edite después cambia su mutation_uuid y no se colapsa.
 * Best-effort: si falla devuelve [] (no se colapsa nada; los siblings re-drenan idempotente).
 */
export async function snapshotSiblingSyncOps (occId, currentSeq, { db = StorageAdapter } = {}) {
  try {
    return (await pendingSiblings(occId, currentSeq, db))
      .map(r => ({ seq: r.seq, mutation_uuid: r.mutation_uuid }))
  } catch (e) {
    dbgWarn('sync', 'snapshotSiblingSyncOps falló (no se colapsan siblings en este envío)', e)
    return []
  }
}
